/**
 * Shared visual primitives for the Compliance Centre (S15).
 *
 * Coverage ring · control-status pill · gap badge · domain bar.
 * Pure SVG / Tailwind — no chart library dependency.
 */
import React from "react";
import { cn } from "@/lib/utils";

/** Circular coverage ring showing a percentage with optional inner label. */
export function CoverageRing({ value, size = 96, stroke = 8, label }) {
  const v = Math.max(0, Math.min(100, value));
  const r = size / 2 - stroke / 2;
  const c = 2 * Math.PI * r;
  const dash = (v / 100) * c;
  const tone =
    v >= 95
      ? { ring: "stroke-risk-low",  text: "text-risk-low"  }
      : v >= 80
        ? { ring: "stroke-tralenex-600", text: "text-tralenex-700" }
        : v >= 60
          ? { ring: "stroke-risk-med", text: "text-risk-med" }
          : { ring: "stroke-risk-high", text: "text-risk-high" };

  return (
    <div className="relative inline-flex items-center justify-center" style={{ width: size, height: size }}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} aria-hidden="true">
        <circle cx={size / 2} cy={size / 2} r={r} className="stroke-border-subtle" strokeWidth={stroke} fill="none" />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          className={tone.ring}
          strokeWidth={stroke}
          fill="none"
          strokeLinecap="round"
          strokeDasharray={`${dash} ${c}`}
          transform={`rotate(-90 ${size / 2} ${size / 2})`}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className={cn("text-h2 font-bold tabular-nums leading-none", tone.text)}>{v}%</span>
        {label && <span className="mt-0.5 text-[10px] font-bold tracking-[0.06em] uppercase text-ink-secondary">{label}</span>}
      </div>
    </div>
  );
}

/** Pill rendering one of: implemented · partial · gap · n/a. */
export function ControlStatusPill({ status }) {
  const cfg = {
    implemented: { label: "Implemented", cls: "bg-risk-low/10 text-risk-low", dot: "bg-risk-low" },
    partial:     { label: "Partial",     cls: "bg-risk-med/15 text-risk-med", dot: "bg-risk-med" },
    gap:         { label: "Gap",         cls: "bg-risk-high/10 text-risk-high", dot: "bg-risk-high" },
    "n-a":       { label: "N/A",         cls: "bg-inset text-ink-secondary", dot: "bg-ink-disabled" },
  }[status] || { label: status, cls: "bg-inset text-ink-secondary", dot: "bg-ink-disabled" };
  return (
    <span
      data-testid={`compliance-status-${status}`}
      className={cn(
        "inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-bold tracking-[0.06em] uppercase",
        cfg.cls,
      )}
    >
      <span className={cn("w-1.5 h-1.5 rounded-full", cfg.dot)} />
      {cfg.label}
    </span>
  );
}

/** Horizontal bar showing met / total for a domain. */
export function DomainBar({ name, met, total }) {
  const pct = total ? Math.round((met / total) * 100) : 0;
  const tone =
    pct >= 95 ? "bg-risk-low" : pct >= 80 ? "bg-tralenex-600" : pct >= 60 ? "bg-risk-med" : "bg-risk-high";
  return (
    <div data-testid={`compliance-domain-${name}`}>
      <div className="flex items-baseline justify-between text-meta">
        <span className="text-ink-primary font-medium truncate">{name}</span>
        <span className="text-ink-secondary tabular-nums shrink-0">
          {met} / {total}
        </span>
      </div>
      <div className="mt-1 h-1.5 bg-inset rounded-full overflow-hidden">
        <div className={cn("h-full rounded-full", tone)} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

/** Small inline badge for open-gap count. */
export function GapBadge({ count }) {
  if (!count) {
    return (
      <span
        data-testid="compliance-gaps-clean"
        className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-risk-low/10 text-risk-low text-meta font-semibold"
      >
        0 gaps
      </span>
    );
  }
  return (
    <span
      data-testid="compliance-gaps-count"
      className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-risk-high/10 text-risk-high text-meta font-semibold tabular-nums"
    >
      {count} {count === 1 ? "gap" : "gaps"}
    </span>
  );
}

/**
 * Sparkline — pure-SVG 12-point trend line with a subtle area fill.
 * Auto-scales to the data range so even flat lines render visibly.
 * The end-cap dot is coloured by direction (up / flat / down).
 */
export function Sparkline({ data, width = 120, height = 32, testId }) {
  if (!data || data.length < 2) return null;
  const min = Math.min(...data);
  const max = Math.max(...data);
  const span = max - min || 1;
  const stepX = width / (data.length - 1);
  const yFor = (v) => height - 2 - ((v - min) / span) * (height - 4);

  const points = data.map((v, i) => [i * stepX, yFor(v)]);
  const linePath = points.map(([x, y], i) => `${i === 0 ? "M" : "L"}${x.toFixed(2)},${y.toFixed(2)}`).join(" ");
  const areaPath = `${linePath} L${width},${height} L0,${height} Z`;

  const delta = data[data.length - 1] - data[0];
  const dir = delta > 1 ? "up" : delta < -1 ? "down" : "flat";
  const tone =
    dir === "up" ? "stroke-risk-low fill-risk-low" :
    dir === "down" ? "stroke-risk-high fill-risk-high" :
    "stroke-tralenex-600 fill-tralenex-600";
  const fillTone =
    dir === "up" ? "fill-risk-low/10" :
    dir === "down" ? "fill-risk-high/10" :
    "fill-tralenex-600/10";
  const [endX, endY] = points[points.length - 1];

  return (
    <svg
      width={width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      role="img"
      aria-label={`12-week coverage trend, ${dir}, change ${delta >= 0 ? "+" : ""}${delta}%`}
      data-testid={testId}
      data-direction={dir}
    >
      <path d={areaPath} className={cn("stroke-none", fillTone)} />
      <path d={linePath} className={cn("fill-none", tone)} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      <circle cx={endX} cy={endY} r="2.5" className={cn("stroke-raised", tone)} strokeWidth="1.5" />
    </svg>
  );
}

/** Inline label with arrow + delta number, paired with `Sparkline`. */
export function TrendCaption({ data }) {
  if (!data || data.length < 2) return null;
  const delta = data[data.length - 1] - data[0];
  const dir = delta > 1 ? "up" : delta < -1 ? "down" : "flat";
  const cfg = {
    up:   { label: "improving", arrow: "↗", cls: "text-risk-low"   },
    down: { label: "declining", arrow: "↘", cls: "text-risk-high" },
    flat: { label: "flat",      arrow: "→", cls: "text-ink-secondary" },
  }[dir];
  return (
    <span
      className={cn("inline-flex items-center gap-1 text-meta font-medium tabular-nums", cfg.cls)}
      data-testid="sparkline-caption"
      data-direction={dir}
    >
      <span aria-hidden="true">{cfg.arrow}</span>
      {cfg.label}
      <span className="text-ink-disabled">·</span>
      <span>{delta >= 0 ? "+" : ""}{delta}% / 12w</span>
    </span>
  );
}
